import React, { useEffect, useMemo, useState } from 'react';
import './styles.scss';
import Box from 'Components/Atoms/Box';
import Typography from 'Components/Atoms/Typography';
import Modal from 'Components/Molecules/Modal';
import Button from 'Components/Atoms/Button';
import { Reservation } from 'types/reservations';
import { ReservationShift } from 'types/shifts';
import { blockToTime, noop } from 'utils/helper';
import {
  groupTableBlocks,
  shiftBlockableForTable,
  tableBlockWindowKey,
} from 'utils/tableBlocks';
import ShiftBlockTimeline, {
  SegmentState,
  useShiftSegments,
} from 'App/ReservationBook/Components/ShiftBlockTimeline';

export type TableBlockSubmit = {
  /** Block the whole day (true) or lift the existing full-day block (false). */
  fullDay: boolean;
  /** Shift ids that should end up blocked. */
  shiftIds: string[];
  /** Ids of blocks that don't match a shift and were ticked off. */
  removeOtherIds: string[];
};

export type TableBlockModalProps = {
  open: boolean;
  tableName: string;
  /** Space + occasions of the table decide which shifts can reach it. */
  table: { space: string; occassions: string[] } | null;
  shifts: ReservationShift[];
  blocks: Reservation[];
  /** Shift to preselect, i.e. the one the user currently has open. */
  defaultShiftId: string | null;
  onClose: () => void;
  onSubmit: (payload: TableBlockSubmit) => void | Promise<void>;
};

const TableBlockModal = ({
  open,
  tableName,
  table,
  shifts,
  blocks,
  defaultShiftId,
  onClose = noop,
  onSubmit = noop,
}: TableBlockModalProps) => {
  const [fullDay, setFullDay] = useState(false);
  const [shiftIds, setShiftIds] = useState<string[]>([]);
  const [removeOtherIds, setRemoveOtherIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  // Only shifts that can actually reach this table (same rule as the grid's
  // reservability map) — a Kino-only shift has no business on a dining table.
  const blockableShifts = useMemo(
    () =>
      table ? shifts.filter((s) => shiftBlockableForTable(s, table)) : shifts,
    [shifts, table]
  );

  const { segments, range } = useShiftSegments(shifts, blockableShifts);

  const existing = useMemo(
    () => groupTableBlocks(blocks, shifts, table),
    [blocks, shifts, table]
  );

  // Content key of the current blocks: the live snapshot produces fresh array
  // identities on every unrelated reservation change, and resetting on those
  // would wipe the user's in-progress selection.
  const existingKey = useMemo(
    () =>
      blocks
        .map((b) => `${b.id}:${b.startTimeInBlocks}-${b.endTimeInBlocks}`)
        .sort()
        .join('|'),
    [blocks]
  );

  // Re-runs when this table's blocks REALLY change while the dialog is open
  // (second device, assistant): the selection resets to the live state instead
  // of a save later deleting a foreign block the user never touched.
  useEffect(() => {
    if (!open) return;

    const blockedShiftIds = Object.keys(existing.byShiftId);
    // Preselect the WHOLE window group of the open shift — its window
    // siblings share the block document, so they must select together.
    const defaultSegment = segments.find((s) => s.shift.id === defaultShiftId);
    const preselect =
      !existing.fullDay &&
      !blockedShiftIds.length &&
      !existing.other.length &&
      defaultSegment
        ? segments
            .filter(
              (s) =>
                tableBlockWindowKey(s.fields) ===
                tableBlockWindowKey(defaultSegment.fields)
            )
            .map((s) => s.shift.id)
        : blockedShiftIds;

    setFullDay(!!existing.fullDay);
    setShiftIds(preselect);
    setRemoveOtherIds([]);
    // eslint-disable-next-line
  }, [open, existingKey]);

  // Shifts sharing a time window share one block document, so they can only
  // toggle together — otherwise "aufheben" on one of them is a silent no-op
  // while its window sibling keeps the document alive.
  const windowGroup = (id: string) => {
    const tapped = segments.find((s) => s.shift.id === id);
    if (!tapped) return [id];
    const key = tableBlockWindowKey(tapped.fields);
    return segments
      .filter((s) => tableBlockWindowKey(s.fields) === key)
      .map((s) => s.shift.id);
  };

  const toggleShift = (id: string) => {
    const group = windowGroup(id);

    // Tapping a shift while the whole day is blocked splits the day up: the
    // full-day block goes, every other window keeps its own block.
    if (fullDay) {
      setFullDay(false);
      setShiftIds(
        segments
          .map((s) => s.shift.id)
          .filter((shiftId) => !group.includes(shiftId))
      );
      return;
    }

    const next = shiftIds.includes(id)
      ? shiftIds.filter((x) => !group.includes(x))
      : [...shiftIds, ...group.filter((x) => !shiftIds.includes(x))];

    // Tapping the split shift back restores the full-day block instead of
    // replacing it with one block per shift.
    if (existing.fullDay && next.length === segments.length) {
      setFullDay(true);
      setShiftIds([]);
      return;
    }

    setShiftIds(next);
  };

  const stateOf = (id: string): SegmentState => {
    const wasBlocked = !!existing.byShiftId[id] || !!existing.fullDay;
    const willBlock = fullDay || shiftIds.includes(id);

    if (willBlock) return wasBlocked ? 'locked' : 'willLock';
    return wasBlocked ? 'willUnlock' : 'free';
  };

  const summary = useMemo(() => {
    const willLock: string[] = [];
    const willUnlock: string[] = [];

    if (fullDay && !existing.fullDay) willLock.push('Ganzer Tag');
    if (!fullDay && existing.fullDay) willUnlock.push('Ganzer Tag');

    // One entry per time window: shifts sharing a window are one block, so
    // they read as one item ("Früh, Kombi (11:00 bis 23:00)") instead of
    // repeating the same times per shift.
    const byWindow: Record<
      string,
      { names: string[]; range: string; lock: boolean }
    > = {};

    segments.forEach(({ shift, fields }) => {
      const wasBlocked = !!existing.byShiftId[shift.id];
      const willBlock = !fullDay && shiftIds.includes(shift.id);
      if (willBlock === wasBlocked) return;

      const key = `${tableBlockWindowKey(fields)}-${willBlock}`;
      if (!byWindow[key]) {
        byWindow[key] = {
          names: [],
          range: `${blockToTime(fields.startTimeInBlocks)} bis ${blockToTime(
            fields.endTimeInBlocks
          )}`,
          lock: willBlock,
        };
      }
      byWindow[key].names.push(shift.name ?? shift.id);
    });

    Object.values(byWindow).forEach((entry) => {
      const label = `${entry.names.join(', ')} (${entry.range})`;
      if (entry.lock) willLock.push(label);
      else willUnlock.push(label);
    });

    return { willLock, willUnlock };
  }, [fullDay, shiftIds, segments, existing]);

  const nothingToDo =
    !summary.willLock.length &&
    !summary.willUnlock.length &&
    !removeOtherIds.length;

  const handleSubmit = async () => {
    setSaving(true);
    try {
      // Callers surface write failures via toast themselves.
      await onSubmit({
        fullDay,
        shiftIds: fullDay ? [] : shiftIds,
        removeOtherIds,
      });
    } finally {
      setSaving(false);
      onClose();
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Tisch ${tableName} sperren`}
      maxWidth="sm"
      fullWidth
      className="tableBlockModal"
      actions={[
        { id: 'cancel', label: 'Abbrechen', onClick: onClose },
        {
          id: 'save',
          label: 'Speichern',
          variant: 'primary',
          disabled: nothingToDo || saving,
          loading: saving,
          onClick: handleSubmit,
        },
      ]}
    >
      {!range ? (
        <Typography variant="text-2" color="subdued">
          Für diesen Tag gibt es keine Schicht, die gesperrt werden kann.
        </Typography>
      ) : (
        <>
          <Typography variant="text-3" color="subdued" className="tbm-hint">
            Schicht antippen zum Sperren, gesperrte Schicht antippen zum Aufheben.
          </Typography>

          <ShiftBlockTimeline
            segments={segments}
            range={range}
            stateOf={stateOf}
            onToggle={toggleShift}
          />

          {!!existing.other.length && (
            <div className="tbm-other">
              <Typography variant="text-3" color="subdued" translation={null}>
                Weitere Sperren an diesem Tisch
              </Typography>
              {existing.other.map((block) => (
                <div
                  className={`tbm-otherRow${
                    removeOtherIds.includes(block.id) ? ' willRemove' : ''
                  }`}
                  key={block.id}
                >
                  <Typography variant="text-2" translation={null}>
                    {blockToTime(block.startTimeInBlocks)} bis{' '}
                    {blockToTime(block.endTimeInBlocks)}
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={() =>
                      setRemoveOtherIds((prev) =>
                        prev.includes(block.id)
                          ? prev.filter((id) => id !== block.id)
                          : [...prev, block.id]
                      )
                    }
                    translation={null}
                  >
                    {removeOtherIds.includes(block.id)
                      ? 'Doch behalten'
                      : 'Aufheben'}
                  </Button>
                </div>
              ))}
            </div>
          )}

          <div className="tbm-summary">
            {nothingToDo ? (
              <Typography variant="text-3" color="subdued" translation={null}>
                Noch nichts geändert.
              </Typography>
            ) : (
              <>
                {!!summary.willLock.length && (
                  <Typography
                    variant="text-3"
                    translation={null}
                    className="tbm-summaryLine"
                  >
                    Sperrt: {summary.willLock.join(', ')}
                  </Typography>
                )}
                {!!summary.willUnlock.length && (
                  <Typography
                    variant="text-3"
                    translation={null}
                    className="tbm-summaryLine"
                  >
                    Hebt auf: {summary.willUnlock.join(', ')}
                  </Typography>
                )}
                {!!removeOtherIds.length && (
                  <Typography
                    variant="text-3"
                    translation={null}
                    className="tbm-summaryLine"
                  >
                    Hebt auf: {removeOtherIds.length} weitere Sperre
                    {removeOtherIds.length > 1 ? 'n' : ''}
                  </Typography>
                )}
              </>
            )}
          </div>
        </>
      )}
    </Modal>
  );
};

export default TableBlockModal;
